import type { OutputOf } from "@gqlb/core";
import type React from "react";
import { useCallback, useRef } from "react";
import { Macros_Fragment } from "@/graphql/fragments";

export { Macros_Fragment };

// ---------------------------------------------------------------------------
// Animated number display — no useEffect, driven by ref mutation check
// ---------------------------------------------------------------------------

const AnimatedValue: React.FC<{
  value: number;
  digits?: number;
  unit?: string;
}> = ({ value, digits = 0, unit }) => {
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastValueRef = useRef<number | null>(null);

  // Use callback ref to trigger animation on every render where value changed
  const setRef = useCallback(
    (el: HTMLSpanElement | null) => {
      spanRef.current = el;
      if (!el) return;

      // Cancel any in-flight animation
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }

      // On first mount, just set the value
      if (lastValueRef.current === null) {
        el.innerText = value.toFixed(digits);
        lastValueRef.current = value;
        return;
      }

      // If value hasn't changed, nothing to animate
      if (lastValueRef.current === value) return;

      const start = lastValueRef.current;
      lastValueRef.current = value;
      const delta = value - start;
      if (delta === 0) return;

      const duration = 400;
      let current = start;
      const stepPerMs = delta / duration;
      let lastFrame = Date.now();

      const animate = () => {
        const now = Date.now();
        const dt = now - lastFrame;
        lastFrame = now;
        current += stepPerMs * dt;

        if (spanRef.current) {
          if ((delta > 0 && current < value) || (delta < 0 && current > value)) {
            spanRef.current.innerText = current.toFixed(digits);
            frameRef.current = requestAnimationFrame(animate);
          } else {
            spanRef.current.innerText = value.toFixed(digits);
            frameRef.current = null;
          }
        }
      };

      frameRef.current = requestAnimationFrame(animate);
    },
    [value, digits],
  );

  return (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>
      <span ref={setRef}>{value.toFixed(digits)}</span>
      {unit && <span className="text-xs text-gray-400">{unit}</span>}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Single macro pill
// ---------------------------------------------------------------------------

const MacroPill: React.FC<{
  label: string;
  value: number;
  unit?: string;
  digits?: number;
  accent?: boolean;
}> = ({ label, value, unit, digits = 0, accent }) => (
  <div
    className={`flex flex-col items-center justify-center rounded-xl px-3 py-2 min-w-0 flex-1 ${
      accent ? "bg-brand-mint/15" : "bg-gray-50"
    }`}
  >
    <span
      className={`text-lg font-bold leading-tight ${accent ? "text-brand-black" : "text-brand-black"}`}
    >
      <AnimatedValue value={value} digits={digits} unit={unit} />
    </span>
    <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 mt-0.5">
      {label}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// MacrosNew — pinned summary bar
// ---------------------------------------------------------------------------

export const MacrosNew: React.FC<{
  data: OutputOf<typeof Macros_Fragment>;
}> = ({ data }) => (
  <div className="flex items-stretch gap-2">
    <MacroPill label="Cal" value={data.calories} digits={0} accent />
    <MacroPill label="Protein" value={data.protein} digits={0} unit="g" />
    <MacroPill label="Carbs" value={data.carbs} digits={0} unit="g" />
    <MacroPill label="Fat" value={data.fat} digits={0} unit="g" />
  </div>
);
